import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";
import { Link } from "react-router-dom";
import { ArrowRight, Cable, CheckCircle2, XCircle, AlertCircle, Clock } from "lucide-react";

const STATE_CFG = {
  connected: { label: "Conectadas", Icon: CheckCircle2, color: "text-emerald-400" },
  disconnected: { label: "Desconectadas", Icon: XCircle, color: "text-zinc-400" },
  error: { label: "Con error", Icon: AlertCircle, color: "text-red-400" },
  not_implemented: { label: "Pendientes", Icon: Clock, color: "text-amber-400" },
};

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    api.get("/system/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/connections").then((r) => setConnections(r.data)).catch(() => {});
  }, []);

  const total = connections.length;
  const byState = status?.connections_by_state || {};

  return (
    <div data-testid={TID.dashboard.root}>
      <PageHeader
        eyebrow="Panel principal"
        title="Dashboard"
        description="Vista general de tus conexiones, integraciones y módulos preparados."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId={TID.dashboard.stat("total")}
          label="Conexiones totales"
          value={total}
          hint="Registradas en el panel"
          Icon={Cable}
        />
        {Object.entries(STATE_CFG).map(([key, cfg]) => (
          <StatCard
            key={key}
            testId={TID.dashboard.stat(key)}
            label={cfg.label}
            value={byState[key] ?? 0}
            Icon={cfg.Icon}
            iconClass={cfg.color}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-md border border-zinc-800 bg-zinc-950 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
                Módulos
              </div>
              <h3 className="text-lg font-semibold text-white">Estado de arquitectura</h3>
            </div>
          </div>
          <div className="space-y-2">
            {status?.modules &&
              Object.entries(status.modules).map(([key, m]) => (
                <ModuleRow key={key} name={key} module={m} />
              ))}
          </div>
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-6">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
            Rápido
          </div>
          <h3 className="text-lg font-semibold text-white">Acciones</h3>
          <div className="mt-4 space-y-2">
            <QuickLink to="/connections" label="Gestionar conexiones" />
            <QuickLink to="/n8n" label="Configurar n8n" />
            <QuickLink to="/krai-api" label="Conectar con KRAI API" />
            <QuickLink to="/settings" label="Configuración" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, Icon, testId, iconClass = "text-zinc-300" }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-zinc-800 bg-zinc-950 p-5 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {label}
        </div>
        {Icon && <Icon className={`h-4 w-4 ${iconClass}`} />}
      </div>
      <div className="text-3xl font-semibold tracking-tight text-white font-mono">
        {value}
      </div>
      {hint && <div className="text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

const MODULE_STATE = {
  ready: { label: "Listo", color: "bg-emerald-500", text: "text-emerald-400" },
  configured: { label: "Configurado", color: "bg-emerald-500", text: "text-emerald-400" },
  not_configured: { label: "Sin configurar", color: "bg-zinc-500", text: "text-zinc-400" },
  not_implemented: { label: "No implementado", color: "bg-amber-500", text: "text-amber-400" },
};

function ModuleRow({ name, module }) {
  const cfg = MODULE_STATE[module.status] || MODULE_STATE.not_configured;
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-zinc-900 last:border-0">
      <div className="min-w-0">
        <div className="text-sm text-white capitalize font-medium">{name.replace(/_/g, " ")}</div>
        <div className="text-xs text-zinc-500">{module.detail}</div>
      </div>
      <div className={`inline-flex items-center gap-2 text-[11px] ${cfg.text} shrink-0`}>
        <span className={`krai-dot ${cfg.color}`} />
        {cfg.label}
      </div>
    </div>
  );
}

function QuickLink({ to, label }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between px-3 py-2.5 rounded-md border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-sm text-zinc-300 hover:text-white transition-colors duration-150"
    >
      {label}
      <ArrowRight className="h-4 w-4 text-zinc-500" />
    </Link>
  );
}
